import React, { useEffect, useRef } from 'react';
import {
  Button,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  Gutter,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ParagraphHeader,
  Radio,
  Stretched,
} from '@axiom/ui';
import type { ProjectNeedsWithLegalSkills } from '@axiom/validation';

import type { DirectEngagementFormDeadEndForm } from './direct-engagements-form-dead-end-modal-schema';
import { DirectEngagementsFormDeadEndModalSchema } from './direct-engagements-form-dead-end-modal-schema';
import type { DirectEngagementFormDeadEndModalTypes } from './direct-engagements-form-dead-end-modal.types';

type ProjectNeedsWithLegalSkillsKeys = keyof ProjectNeedsWithLegalSkills;
export interface DirectEngagementsFormDeadEndModalProps {
  modalType: DirectEngagementFormDeadEndModalTypes;
  title: string;
  content: string;
  radioOptions: ReadonlyArray<{
    value: string;
    fieldName: ProjectNeedsWithLegalSkillsKeys;
    fieldValue: number | string;
  }>;
  onClose: (modalType?: DirectEngagementFormDeadEndModalTypes) => void;
  onSubmit: (
    fieldName: keyof ProjectNeedsWithLegalSkills,
    fieldValue: string | number
  ) => void;
}

export const DirectEngagementsFormDeadEndModal: React.FC<
  DirectEngagementsFormDeadEndModalProps
> = ({ modalType, title, content, radioOptions, onClose, onSubmit }) => {
  const initialRadioSelection = radioOptions[0]?.fieldName;
  const radioOptionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (radioOptionRef.current) {
      // Adding focus on the modal to prevent user interacting with background form while modal is open
      radioOptionRef.current.focus();
    }
  }, []);

  const handleSubmit = (formData: Partial<DirectEngagementFormDeadEndForm>) => {
    const selectedOption = radioOptions.find(
      radioOption => radioOption.fieldName === formData.radioSelection
    );
    if (selectedOption) {
      onSubmit(selectedOption.fieldName, selectedOption.fieldValue);
    }
  };

  const initialValues = { radioSelection: initialRadioSelection };

  return (
    <Form
      name="deadend-form"
      schema={DirectEngagementsFormDeadEndModalSchema}
      initialValues={initialValues}
      onSubmit={formData => {
        handleSubmit({ ...initialValues, ...formData });
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="deadend-modal">
          <ModalHeader
            name={`deadend-modal-header-${modalType}`}
            onClose={() => {
              onClose(modalType);
            }}
          >
            {title}
          </ModalHeader>
          <ModalSection name={`deadend-modal-section-${modalType}`}>
            <FormGroup label={content}>
              <Stretched>
                <div
                  tabIndex={-1}
                  ref={radioOptionRef}
                  data-test="radio-wrapper"
                >
                  <Grid>
                    {radioOptions.map((radioOption, index) => (
                      <GridColumn
                        key={radioOption.fieldName}
                        largeScreenWidth={6}
                        tabletWidth={6}
                        mobileWidth={12}
                        stretched
                      >
                        <Radio
                          mode="tile"
                          name="radioSelection"
                          displayValue={
                            <ParagraphHeader>
                              {radioOption.value}
                            </ParagraphHeader>
                          }
                          option={radioOption.fieldName}
                        />
                        {index < radioOptions.length - 1 && (
                          <Gutter bottom="16px" only="mobile" />
                        )}
                      </GridColumn>
                    ))}
                  </Grid>
                </div>
              </Stretched>
            </FormGroup>
          </ModalSection>
          <ModalFooter>
            <Button name="deadend-submit" onClick={fireSubmit}>
              Confirm Change
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
